import { useMemo, useState } from 'react';
import { GAMES } from '../games/catalog';
import { arcadeAudio } from '../platform/audio';
import { getStamps, stampCount } from '../platform/stamps';
import { bestScore } from '../platform/storage';
import type { GameId, GameMeta } from '../platform/types';

function Ticket({
  g,
  featured,
  onPlay,
}: {
  g: GameMeta;
  featured?: boolean;
  onPlay: (id: GameId) => void;
}) {
  const s = getStamps(g.id);
  const best = bestScore(g.id);
  return (
    <article
      className={featured ? 'ticket featured' : 'ticket'}
      style={{ ['--accent' as string]: g.accent, ['--glow' as string]: g.glow }}
    >
      <div className="ticket-art">
        <img src={g.art} alt="" />
        <span className="ticket-genre">{g.genre}</span>
      </div>
      <div className="ticket-body">
        <div className="ticket-meta">
          <span>{g.minutes}</span>
          <span className="best">{best ? best.toLocaleString() : 'unplayed'}</span>
        </div>
        <h2>{g.title}</h2>
        <p>{g.tagline}</p>
        <div className="ticket-foot">
          <div className="stamps" aria-label="stamps">
            <i className={s.clear ? 'on' : ''} title="Clear">C</i>
            <i className={s.skill ? 'on' : ''} title="Skill">S</i>
            <i className={s.mastery ? 'on' : ''} title="Mastery">M</i>
          </div>
          <button type="button" className="play" onClick={() => onPlay(g.id)}>
            Sit down
          </button>
        </div>
      </div>
    </article>
  );
}

export function Portal({ onPlay }: { onPlay: (id: GameId) => void }) {
  const [filter, setFilter] = useState('all');
  const [muted, setMuted] = useState(arcadeAudio.settings.muted);
  const stamps = stampCount();
  const genres = useMemo(() => ['all', ...new Set(GAMES.map((g) => g.genre))], []);
  const shown = filter === 'all' ? GAMES : GAMES.filter((g) => g.genre === filter);
  const feature = shown[0];
  const rest = shown.slice(1);

  return (
    <div className="portal">
      <div className="sky" aria-hidden>
        <i className="lantern-hang a" />
        <i className="lantern-hang b" />
        <i className="lantern-hang c" />
      </div>
      <div className="grain" />

      <header className="topbar">
        <div className="brand">
          <span className="mark" aria-hidden />
          <div>
            <p className="eyebrow">Grok 4.6 · Casper Studios</p>
            <strong className="wordmark">Lumen Arcade</strong>
          </div>
        </div>
        <div className="top-actions">
          <span className="stamp-count">{stamps} / 21 stamps</span>
          <button
            type="button"
            className="ghost"
            onClick={() => setMuted(arcadeAudio.toggleMute())}
          >
            {muted ? 'Sound off' : 'Sound on'}
          </button>
        </div>
      </header>

      <section className="hero">
        <div className="hero-copy">
          <p className="eyebrow">Night market · seven cabinets</p>
          <h1>Sit down. Leave a stamp. Come back warmer.</h1>
          <p className="lede">
            A private arcade under paper lanterns. No accounts, no ads — just full runs
            that last ten to fifteen minutes, painted with Kenney wood and felt.
          </p>
          <div className="hero-pills">
            <span>{GAMES.length} titles</span>
            <span>Desktop first</span>
            <span>Phone ready</span>
          </div>
        </div>
        {feature && <Ticket g={feature} featured onPlay={onPlay} />}
      </section>

      <nav className="filters" aria-label="Filter by genre">
        {genres.map((g) => (
          <button
            key={g}
            type="button"
            className={filter === g ? 'chip on' : 'chip'}
            onClick={() => setFilter(g)}
          >
            {g}
          </button>
        ))}
      </nav>

      <section className="grid">
        {rest.map((g) => (
          <Ticket key={g.id} g={g} onPlay={onPlay} />
        ))}
      </section>

      <footer className="credits">
        <div className="credit-mark" aria-hidden />
        <div>
          <strong>Credits</strong>
          <p>
            Art and audio by Kenney (CC0) and the shared poker-card set. Key art painted
            for this lobby. Type: Fraunces, Figtree, IBM Plex Mono. Built by Grok 4.6
            for Casper Studios.
          </p>
        </div>
        <p className="tiny">Cloudflare project lumen-arcade-grok-4-6 · new resources only</p>
      </footer>
    </div>
  );
}
